<template>
  <div class="analysis-card">
    <div class="analysis-card__info">
      <div class="analysis-card__info--title">
        <div class="analysis-card__info--title-main">
          <i class="t-iconfont" :class="dataCompt.render.icon"></i>
          <span class="title-text">
            {{ dataCompt.titleText }}
          </span>
        </div>
        <span v-if="dataCompt.deductionScoreText" class="deduction-score">
          {{ dataCompt.deductionScoreText }}
        </span>
      </div>
      <div v-if="dataCompt.descriptionText" class="analysis-card__info--content">
        {{ dataCompt.descriptionText }}
      </div>
    </div>
    <button
      v-if="dataCompt.render.isBad && dataCompt.quoteContent"
      class="analysis-card__quote"
      type="button"
      @click="handleViewQuote"
    >
      <div class="analysis-card__quote--header">
        <span class="quote-label">销售原话</span>
        <span v-if="dataCompt.quoteTime" class="quote-time">{{ dataCompt.quoteTime }}</span>
      </div>
      <div class="analysis-card__quote--content">{{ dataCompt.quoteContent }}</div>
    </button>
    <div v-if="dataCompt.render.isBad && dataCompt.suggestion" class="analysis-card__suggestion">
      <div class="analysis-card__suggestion--title">
        <i class="icon-sparkling-2-fill t-iconfont"></i>
        <span class="suggestion-label">AI 建议</span>
      </div>
      <div class="suggestion-content">{{ dataCompt.suggestion }}</div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed, defineProps } from 'vue'

const emit = defineEmits(['view-quote'])
// props
const props = defineProps({
  dataInfo: {
    type: Object,
    default: () => ({
      status: 'good',
      title: '',
      content: '',
      suggestion: '',
      result: '',
      originalQuote: '',
      deductionTitle: '',
      deductionScore: 0,
      index: -1
    })
  },
  messageList: {
    type: Array,
    default: () => []
  }
})

const getQuoteTime = (dataInfo = {}) => {
  const messageItem = props.messageList?.[dataInfo.index]
  if (messageItem?.time) {
    return messageItem.time
  }

  const timeMatch = String(dataInfo.originalQuote || '').match(/(\d{2}:\d{2}:\d{2})/)
  return timeMatch?.[1] || ''
}

const normalizeQuoteContent = (quote = '') => {
  return String(quote)
    .replace(/^[\[【][^\]】]+[\]】]\s*\d{2}:\d{2}:\d{2}\s*/, '')
    .trim()
}

const handleViewQuote = () => {
  emit('view-quote', props.dataInfo.index)
}

// computed
const dataCompt = computed(() => {
  const status = props.dataInfo.status
  const renderMap = {
    good: {
      isGood: true,
      icon: 'icon-emotion-happy-line'
    },
    bad: {
      isBad: true,
      icon: 'icon-emotion-unhappy-line'
    }
  }
  const hasTitle = Boolean(props.dataInfo.deductionTitle || props.dataInfo.title)
  const deductionScore = Number(props.dataInfo.deductionScore)
  const deductionScoreText =
    Number.isFinite(deductionScore) && status === 'bad' ? `-${Math.abs(deductionScore)}` : ''

  return {
    ...props.dataInfo,
    titleText: props.dataInfo.deductionTitle || props.dataInfo.title || props.dataInfo.result || '',
    descriptionText:
      status === 'bad'
        ? hasTitle
          ? props.dataInfo.result || ''
          : ''
        : props.dataInfo.title
          ? props.dataInfo.result || ''
          : props.dataInfo.originalQuote || props.dataInfo.content || props.dataInfo.result || '',
    quoteTime: getQuoteTime(props.dataInfo),
    quoteContent: normalizeQuoteContent(props.dataInfo.originalQuote),
    deductionScoreText,
    render: renderMap[status] || {}
  }
})
// lice-cycle
onMounted(() => {})
</script>

<style lang="less" scoped>
.analysis-card {
  &__info {
    &--title {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 8px;

      .deduction-score {
        flex-shrink: 0;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        color: #f54e4e;
      }
    }

    &--title-main {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: flex-start;
      gap: 4px;
    }

    .title-text {
      line-height: 20px;
      font-size: 14px;
      font-weight: 600;
      color: #323233;
      word-break: break-word;
    }

    .t-iconfont {
      flex-shrink: 0;
      height: 20px;
      line-height: 20px;
      font-size: 18px;
      width: 20px;
      text-align: center;
    }

    .icon-emotion-happy-line {
      color: #00c241;
    }

    .icon-emotion-unhappy-line {
      color: #f54e4e;
    }

    &--content {
      margin: 2px 0 0 24px;
      font-size: 14px;
      color: #323233;
      line-height: 20px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  &__quote {
    margin-top: 8px;
    margin-left: 24px;
    padding: 0 0 0 12px;
    border: none;
    border-left: 2px solid #c8c9cc;
    background: transparent;
    width: calc(100% - 24px);
    text-align: left;
    cursor: pointer;

    &--header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .quote-label {
      flex: 1;
      min-width: 0;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: #969799;
    }

    .quote-time {
      flex-shrink: 0;
      padding: 2px 8px;
      border-radius: 24px 4px 4px 24px;
      background: #f5e8ff;
      font-size: 12px;
      line-height: 16px;
      color: #7a52f4;
    }

    &--content {
      margin-top: 4px;
      font-size: 14px;
      line-height: 20px;
      color: #969799;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  &__suggestion {
    border: 1px solid #f5e8ff;
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.05);
    background: #fff;
    padding: 12px;
    box-sizing: border-box;
    margin-top: 8px;
    margin-left: 24px;
    display: flex;
    flex-direction: column;
    border-radius: 12px;

    &--title {
      display: flex;
      align-items: center;
    }

    .suggestion-label {
      margin-left: 8px;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      background-clip: text;
      color: transparent;
      background-image: linear-gradient(314deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
    }

    .t-iconfont {
      font-size: 18px;
      text-align: center;
      background-clip: text;
      color: transparent;
      background-image: linear-gradient(314deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
    }

    .suggestion-content {
      margin-top: 4px;
      font-size: 13px;
      color: #323233;
      line-height: 18px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
}
</style>
